import React, { useState } from "react";
import {
  // Science/Chemistry focused
  FlaskConical,
  Beaker,
  Microscope,
  Pipette,
  Droplet,
  Thermometer,
  Pill,
  Atom,
  Dna,
  Syringe,
  Factory,
  // Generic fallbacks
  Shield,
  Target,
  Zap,
} from "lucide-react";

// Lightweight custom SVG icons to extend the chemistry set
const SvgIcon = ({ children, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const MoleculeIcon = (props) => (
  <SvgIcon {...props}>
    <circle cx="6" cy="6" r="2" />
    <circle cx="18" cy="6" r="2" />
    <circle cx="12" cy="18" r="2" />
    <line x1="7.7" y1="7.7" x2="10.5" y2="15.5" />
    <line x1="16.3" y1="7.7" x2="13.5" y2="15.5" />
    <line x1="8" y1="6" x2="16" y2="6" />
  </SvgIcon>
);

const TestTubeIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M9 3h6" />
    <path d="M10 3v11.5a4 4 0 0 1-1.2 2.9L8 18.2a3 3 0 1 0 4.2 4.2l0.6-0.8A6 6 0 0 0 14 17.5V3" />
    <path d="M10 9h4" />
  </SvgIcon>
);

const ColumnIcon = (props) => (
  <SvgIcon {...props}>
    <rect x="8" y="3" width="8" height="18" rx="2" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="13" x2="16" y2="13" />
  </SvgIcon>
);

const BunsenBurnerIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M12 3c2 2 2 4 0 6c-2-2-2-4 0-6z" />
    <path d="M10 11h4v4a2 2 0 0 1-2 2a2 2 0 0 1-2-2v-4z" />
    <path d="M6 21h12" />
  </SvgIcon>
);

const MortarPestleIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M4 12h16v1a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6v-1z" />
    <path d="M16 6l3-3" />
    <path d="M14 8l3-3" />
  </SvgIcon>
);

const SafetyGogglesIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M3 10h18l1 3a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3h-2a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3l1-3z" />
  </SvgIcon>
);

// Chemistry-focused icon set
const availableIcons = [
  { name: "Flask", component: FlaskConical, key: "flask" },
  { name: "Beaker", component: Beaker, key: "beaker" },
  { name: "Microscope", component: Microscope, key: "microscope" },
  { name: "Pipette", component: Pipette, key: "pipette" },
  { name: "Droplet", component: Droplet, key: "droplet" },
  { name: "Thermometer", component: Thermometer, key: "thermometer" },
  { name: "Pill", component: Pill, key: "pill" },
  { name: "Atom", component: Atom, key: "atom" },
  { name: "DNA", component: Dna, key: "dna" },
  { name: "Syringe", component: Syringe, key: "syringe" },
  { name: "Factory", component: Factory, key: "factory" },
  { name: "Shield", component: Shield, key: "shield" },
  { name: "Target", component: Target, key: "target" },
  { name: "Zap", component: Zap, key: "zap" },
  // Custom SVG additions
  { name: "Molecule", component: MoleculeIcon, key: "molecule" },
  { name: "Test Tube", component: TestTubeIcon, key: "testtube" },
  { name: "Chromatography", component: ColumnIcon, key: "column" },
  { name: "Bunsen Burner", component: BunsenBurnerIcon, key: "burner" },
  { name: "Mortar & Pestle", component: MortarPestleIcon, key: "mortar" },
  { name: "Safety Goggles", component: SafetyGogglesIcon, key: "goggles" },
];

export const IconSelector = ({ selectedIcon, onIconSelect, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredIcons = availableIcons.filter(icon =>
    icon.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedIconData = availableIcons.find(icon => icon.key === selectedIcon);

  return (
    <div className="icon-selector" style={{ position: 'relative', zIndex: 10000 }}>
      <label className="form-label">Service Icon</label>

      {/* Selected Icon Display */}
      <div
        className={`icon-display ${error ? 'error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          border: `1px solid ${error ? '#dc3545' : '#dee2e6'}`,
          borderRadius: '4px',
          padding: '10px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          minHeight: '50px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          pointerEvents: 'auto'
        }}
      >
        {selectedIconData ? (
          <>
            <selectedIconData.component size={24} />
            <span>{selectedIconData.name}</span>
          </>
        ) : (
          <span style={{ color: '#6c757d' }}>Select an icon</span>
        )}
        <span style={{ marginLeft: 'auto' }}>▼</span>
      </div>

      {error && <p className="errorMessage">Icon is required</p>}

      {/* Inline Panel (non-overlay, pushes content) */}
      {isOpen && (
        <div
          style={{
            marginTop: '8px',
            zIndex: 1,
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            padding: '12px',
            maxHeight: '320px',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          <input
            type="text"
            placeholder="Search icons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control mb-2"
            style={{ pointerEvents: 'auto' }}
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: '10px'
            }}
          >
            {filteredIcons.map((icon) => (
              <button
                key={icon.key}
                type="button"
                onClick={() => { onIconSelect(icon.key); setIsOpen(false); }}
                title={icon.name}
                style={{
                  padding: '10px',
                  border: `2px solid ${selectedIcon === icon.key ? '#2a3071' : '#dee2e6'}`,
                  borderRadius: '6px',
                  background: selectedIcon === icon.key ? '#f0f4ff' : '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  pointerEvents: 'auto'
                }}
              >
                <icon.component size={22} />
                <span style={{ fontSize: '11px' }}>{icon.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get icon component by key
export const getIconComponent = (iconKey) => {
  const iconData = availableIcons.find(icon => icon.key === iconKey);
  return iconData ? iconData.component : null;
};